import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  of,
  switchMap,
  catchError,
  combineLatest,
  map,
  startWith,
  shareReplay,
} from 'rxjs';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { ThemeMode, ThemeService } from '../../theme/theme.service';
import { AuthFacade } from '../../auth/auth.facade';
import { MeService } from '../../api/me.service';
import { MeResponse } from '../../api/api.types';
import { AdminAccessRequestsService } from '../../api/admin-access-requests.service';

type WorkspaceShellVm = {
  isAuthenticated: boolean;
  pendingRequests: number | null;
  themeMode: ThemeMode;
  avatarInitial: string;
  avatarLabel: string;
};

@Component({
  selector: 'of-workspace-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, MatProgressSpinnerModule],
  template: `
    <ng-container *ngIf="vm$ | async as vm; else loading">
      <div class="forge-shell">
        <div class="forge-shell__noise" aria-hidden="true"></div>

        <main class="forge-main">
          <div class="forge-main__content">
            <router-outlet></router-outlet>
          </div>
        </main>

      </div>
    </ng-container>

    <ng-template #loading>
      <div class="forge-shell">
        <div class="forge-shell__noise" aria-hidden="true"></div>
        <nav class="navbar">
          <a class="logo" routerLink="/">
            <div class="logo-mark">
              <img
                class="logo-img"
                src="/assets/logo-trans.png"
                alt="Omniscient Forge logo"
                width="44"
                height="44"
                decoding="async"
              />
            </div>
            <div class="logo-text">
              <span>OMNISCIENT</span>
              <span class="forge">FORGE</span>
            </div>
          </a>
          <div class="nav-actions">
            <button
              type="button"
              class="icon-button"
              aria-label="Toggle theme"
              (click)="toggleTheme()"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="4.25" />
                <path d="M12 2.5v2.5M12 19v2.5M4.5 12H2M22 12h-2.5M6.4 6.4 4.9 4.9M19.1 19.1l-1.5-1.5M6.4 17.6 4.9 19.1M19.1 4.9l-1.5 1.5" />
              </svg>
            </button>
            <button type="button" class="user-avatar" aria-label="Account" disabled>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="8.5" r="3.5" />
                <path d="M5 19c1.5-2.3 4-3.5 7-3.5s5.5 1.2 7 3.5" />
              </svg>
            </button>
          </div>
        </nav>
        <main class="forge-main">
          <div class="forge-main__content">
            <div class="hero-loading">
              <mat-spinner diameter="18"></mat-spinner>
              <span class="support-text">Loading workspace.</span>
            </div>
          </div>
        </main>
        <footer class="forge-footer">&copy; {{ currentYear }} Omniscient Forge &bull; Crafted for Foundry VTT</footer>
      </div>
    </ng-template>
  `,
})
export class WorkspaceShellComponent {
  private readonly refresh$ = new BehaviorSubject<void>(undefined);
  currentYear = new Date().getFullYear();

  vm$: Observable<WorkspaceShellVm>;

  constructor(
    private readonly theme: ThemeService,
    private readonly auth: AuthFacade,
    private readonly router: Router,
    private readonly me: MeService,
    private readonly adminAccessRequests: AdminAccessRequestsService
  ) {
    const isAuthenticated$ = of(this.auth.isAuthenticated());

    const me$ = this.refresh$.pipe(
      switchMap(() => this.me.getMe(true)),
      startWith(null as MeResponse | null),
      catchError(() => of(null)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const pendingRequests$ = me$.pipe(
      switchMap((me) => {
        if (me?.user.role !== 'global_admin') {
          return of(null as number | null);
        }
        return this.adminAccessRequests.list().pipe(
          map((res) => res.users.filter((user) => user.approvalStatus !== 'approved').length),
          catchError(() => of(null))
        );
      }),
      startWith(null as number | null)
    );

    this.vm$ = combineLatest([isAuthenticated$, me$, pendingRequests$, this.theme.mode$]).pipe(
      map(([isAuthenticated, me, pendingRequests, themeMode]) => {
        const avatarLabel = (me?.user.email ?? me?.user.name ?? 'Account').trim();
        const avatarInitial = avatarLabel.charAt(0).toUpperCase() || '?';

        return {
          isAuthenticated,
          pendingRequests,
          themeMode,
          avatarInitial,
          avatarLabel,
        };
      })
    );
  }

  toggleTheme() {
    this.theme.toggle();
  }

  accountAction(vm: WorkspaceShellVm | null) {
    if (!vm?.isAuthenticated) {
      this.auth.loginToWorkspace();
      return;
    }

    this.auth.logout();
  }
}
